/**
 * Core constants, preference types, and DOM helpers for AccessibilityWidget.
 * Extracted verbatim from AccessibilityWidget.tsx.
 */

/* ── Constants ── */

export const STORAGE_KEY = 'buzzbitx-a11y';
export const DYSLEXIC_FONT_URL =
  'https://cdnjs.cloudflare.com/ajax/libs/open-dyslexic/2.0.0/open-dyslexic-regular.css';

export interface A11yPrefs {
  fontSize: number;       // 100–200
  highContrast: boolean;
  highlightLinks: boolean;
  dyslexiaFont: boolean;
  stopAnimations: boolean;
  readingGuide: boolean;
  bigCursor: boolean;
  textSpacing: boolean;
  grayscale: boolean;
  invertColors: boolean;
  hideImages: boolean;
  keyboardNav: boolean;
}

export const DEFAULT_PREFS: A11yPrefs = {
  fontSize: 100,
  highContrast: false,
  highlightLinks: false,
  dyslexiaFont: false,
  stopAnimations: false,
  readingGuide: false,
  bigCursor: false,
  textSpacing: false,
  grayscale: false,
  invertColors: false,
  hideImages: false,
  keyboardNav: false,
};

/* ── Helpers ── */

export function loadPrefs(): A11yPrefs {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { ...DEFAULT_PREFS, ...JSON.parse(raw) };
  } catch { /* corrupted – fall through */ }
  return { ...DEFAULT_PREFS };
}

export function savePrefs(prefs: A11yPrefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch { /* quota exceeded – ignore */ }
}

/* ── Style-sheet ID constants ── */
export const STYLE_HIGH_CONTRAST   = 'a11y-high-contrast';
export const STYLE_HIGHLIGHT_LINKS = 'a11y-highlight-links';
export const STYLE_STOP_ANIM       = 'a11y-stop-animations';
export const STYLE_BIG_CURSOR      = 'a11y-big-cursor';
export const STYLE_TEXT_SPACING     = 'a11y-text-spacing';
export const STYLE_GRAYSCALE        = 'a11y-grayscale';
export const STYLE_INVERT           = 'a11y-invert';
export const STYLE_HIDE_IMAGES      = 'a11y-hide-images';
export const STYLE_KBD_NAV          = 'a11y-kbd-nav';

export function upsertStyle(id: string, css: string) {
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = css;
}

export function removeStyle(id: string) {
  document.getElementById(id)?.remove();
}

/* ── Reading-guide element ── */
export function ensureReadingGuide(): HTMLDivElement {
  let el = document.getElementById('a11y-reading-guide') as HTMLDivElement | null;
  if (!el) {
    el = document.createElement('div');
    el.id = 'a11y-reading-guide';
    el.style.cssText =
      'position:fixed;left:0;width:100%;height:2px;background:#18181B;pointer-events:none;z-index:99999;transition:top 0.05s linear;display:none;';
    document.body.appendChild(el);
  }
  return el;
}

/* ── Dyslexic font link ── */
export function ensureDyslexicLink(): HTMLLinkElement {
  let el = document.getElementById('a11y-dyslexic-font') as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.id = 'a11y-dyslexic-font';
    el.rel = 'stylesheet';
    el.href = DYSLEXIC_FONT_URL;
    document.head.appendChild(el);
  }
  return el;
}
